import { get, intersection, uniq, without } from 'lodash';
import { STALE_PATHS_KEY } from './constants';
import { NormalisedStoreState } from './types';

// Just remove the store when parsing this file.
localStorage.removeItem(STALE_PATHS_KEY);

/**
 * Checks if a given path is flagged as being stale in the store.
 *
 * @param {string} path
 * @returns {boolean}
 */
export const isCacheStale = (path: string): boolean => getStale().includes(path);

/**
 * This is a more complicated, convoluted way of checking but allows you to just check by the
 * requested entity type and a collection of ids.
 *
 * Firstly it goes through all the paths flagged as stale in the cached requests by entity.
 * Then it pulls all the ids from those requests. Afterwards it flattens this array and
 * intersects it with the provided ids argument. If this has any matches, consider this
 * request failed and request fresh data from the api.
 *
 * @param {NormalisedStoreState} normalised
 * @param {string} entity
 * @param {string[]} ids
 * @returns {boolean}
 */
export const isCacheStaleByIds = (normalised: NormalisedStoreState, entity: string, ids: string[]): boolean => {
  const staleboys = getStale();

  const staleIds = staleboys.reduce(
    (memo: string[], path: string) => memo.concat(...get(normalised, `requests.${entity}.${path}`, [])),
    []
  );

  return intersection(ids, staleIds).length > 0;
};

/**
 * This resolves what paths to add to the stale state. If the given pathOrEntity is found in the
 * entities section, then grab all child urls of that entity to mark as stale. Otherwise add
 * the given path as stale. If this ends up adding junk in here because it matches neither
 * (e.g. if you passed in "SomeUnknownThing" as the pathOrEntity arg) then it doesn't really
 * matter as it won't even match a request being made.
 *
 * @param {NormalisedStoreState} storeState
 * @param {string} pathOrEntity
 * @returns {string[]}
 */
export const resolveCachedUrls = (storeState: NormalisedStoreState, pathOrEntity: string): string[] => {
  const requests = storeState.requests || {};
  return requests.hasOwnProperty(pathOrEntity) ? Object.keys(requests[pathOrEntity]) : [pathOrEntity];
};

/**
 * Retrieves the list of stale paths from local storage.
 *
 * @returns {string[]}
 */
export const getStale = (): string[] => JSON.parse(localStorage.getItem(STALE_PATHS_KEY) || '[]');

/**
 * Removes a given path from the stale paths in local storage.
 *
 * @param {string} path
 */
export const removeFromStale = (path: string): void => {
  const stale = getStale();
  const modified = without(stale, path);
  localStorage.setItem(STALE_PATHS_KEY, JSON.stringify(modified));
};

/**
 * Adds a given path to the set of stale paths in local storage.
 *
 * @param {string} path
 */
export const addStale = (path: string): void => {
  const stale = getStale();
  const modified = uniq(stale.concat(path));
  localStorage.setItem(STALE_PATHS_KEY, JSON.stringify(modified));
};
